import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, addDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  IoArrowBack,
  IoPencil,
  IoQrCodeOutline,
  IoHomeOutline,
  IoPeopleOutline,
  IoPersonOutline,
  IoAdd,
  // eslint-disable-next-line no-unused-vars
  IoCheckmark, // (Make sure to import IoCheckmark)
  IoTimeOutline
} from 'react-icons/io5';
import AccesoModal from '../components/AccesoModal';
import HistorialVisitaModal from '../components/HistorialVisitaModal';
import './VisitasScreen.css'; // --- Premium CSS ---
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function VisitasScreen() {
  const navigate = useNavigate();
  const [visitas, setVisitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentAcceso, setCurrentAcceso] = useState(null);
  const [visitaHistorial, setVisitaHistorial] = useState(null); // --- NEW STATE ---
  const [showSuccessBanner, setShowSuccessBanner] = useState(false); // --- New State ---
  const [activeAccessMap, setActiveAccessMap] = useState({}); // --- New State ---
  const [confirmVisita, setConfirmVisita] = useState(null); // --- New State for Custom Confirm Modal ---
  const { currentUser, userData } = useAuth();

  // --- 1. Fetch Visits Loop (Existing) ---
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "visitasRecurrentes"),
      where("residenteId", "==", currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const visitasDeFirebase = [];
      querySnapshot.forEach((doc) => {
        visitasDeFirebase.push({ ...doc.data(), id: doc.id });
      });
      setVisitas(visitasDeFirebase);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar visitas: ", error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  // --- 2. Fetch Active Accesses Loop (New) ---
  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to active accesses for this user
    // Note: We need a composite index for (residenteId + estatus).
    // If it fails, we can just filter client-side for now if dataset is small,
    // but best practice is query.
    const q = query(
      collection(db, "accesos"),
      where("residenteId", "==", currentUser.uid),
      where("estatus", "==", "Activo")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const map = {};
      const now = new Date();
      snapshot.forEach(doc => {
        const data = doc.data();
        // Check expiration
        if (data.expira?.toDate() > now && data.visitaRecurrenteId) {
          map[data.visitaRecurrenteId] = true;
        }
      });
      setActiveAccessMap(map);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleDarAcceso = (visita) => {
    if (!currentUser) {
      alert("Error: No se ha identificado al usuario.");
      return;
    }
    setConfirmVisita(visita);
  };

  const handleConfirmAcceso = async () => {
    if (!confirmVisita) return;
    const visita = confirmVisita;
    setConfirmVisita(null);

    try {
      const token = Math.floor(100000 + Math.random() * 900000).toString(); // 6 dígitos numéricos
      const expiracion = Timestamp.fromDate(new Date(Date.now() + 6 * 60 * 60 * 1000));
      const accesosRef = collection(db, "accesos");
      const rawCasa = userData?.numeroCasa || userData?.casaNumero || userData?.residencia || userData?.depto || userData?.unidadResidente || userData?.unidad || '';
      const cleanCasa = (rawCasa && rawCasa !== 'S/N' && rawCasa !== 'N/A' && rawCasa !== 'Sin Unidad') ? String(rawCasa).replace(/^(casa|depto)\s*/i, '').trim() : '';

      await addDoc(accesosRef, {
        token: token, tipo: 'Visita', nombreVisita: visita.nombreCompleto,
        visitaRecurrenteId: visita.id,
        residenteId: currentUser.uid,
        residenteNombre: userData?.nombre || '',
        condominioId: userData?.condominioId || '',
        cuentaPadreId: userData?.cuentaPadreId || null,
        numeroCasa: cleanCasa,
        fechaCreacion: Timestamp.now(),
        expira: expiracion, usosMaximos: 1, usosActuales: 0, estatus: 'Activo'
      });
      const accesoParaModal = {
        token: token, tipo: 'Visita', nombreVisita: visita.nombreCompleto
      };

      setCurrentAcceso(accesoParaModal);
      setShowSuccessBanner(true); // --- Show Success ---

    } catch (e) {
      console.error("Error al dar acceso: ", e);
      alert("Error al generar el código de acceso.");
    }
  };

  const getVisitaIcon = (parentesco) => {
    if (parentesco === 'Familiar') return { icon: <IoHomeOutline />, className: 'familiar' };
    if (parentesco === 'Amistad') return { icon: <IoPeopleOutline />, className: 'amistad' };
    return { icon: <IoPersonOutline />, className: 'otro' };
  };

  return (
    <>
      <KondoBrandHeader showBack={false} />
      <div className="visitas-container">
        {/* Sticky Compact Header */}
        <div className="visitas-fixed-wrapper">
          <div className="visitas-banner-compact">
            <button onClick={() => navigate('/accesos')} className="visitas-back-btn glass">
              <IoArrowBack size={24} />
            </button>

            <div className="visitas-banner-content">
              <h2>Control de Visitas</h2>
              <p>Tus invitados frecuentes</p>
            </div>

            <button className="visitas-action-btn glass" onClick={() => navigate('/accesos/visitas/nueva')}>
              <IoAdd size={24} />
            </button>

            <div className="visitas-banner-icon">
              <IoPeopleOutline />
            </div>
          </div>
        </div>

        <div className="visita-card">
          {loading && <div className="empty-state">Cargando visitas...</div>}

          {!loading && visitas.length === 0 && (
            <div className="empty-state">
              <IoPeopleOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
              <p>Aún no tienes visitas recurrentes.</p>
            </div>
          )}

          {!loading && visitas.length > 0 && (
            <div className="visitas-list">
              {visitas.map((visita) => {
                const { icon, className } = getVisitaIcon(visita.parentesco);
                const hasActiveAccess = activeAccessMap[visita.id];

                return (
                  <div key={visita.id} className="visita-card-item">
                    <div className="visita-header-row">
                      <div className={`visita-avatar-frame ${className}`}>
                        {icon}
                      </div>

                      <div className="visita-info-col">
                        <span className="visita-name">{visita.nombreCompleto}</span>
                        <div className="visita-meta-row">
                          <span className={`visita-badge ${visita.parentesco}`}>{visita.parentesco}</span>
                          {hasActiveAccess && (
                            <span className="active-access-tag">
                              <IoTimeOutline size={10} style={{ marginRight: 2 }} /> OK
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="visita-actions-row">
                      <button className="visita-action-btn history" title="Ver Historial" onClick={() => setVisitaHistorial(visita)}>
                        <IoTimeOutline size={18} />
                      </button>
                      <button className="visita-action-btn edit" title="Editar" onClick={() => navigate(`/accesos/visitas/editar/${visita.id}`)}>
                        <IoPencil size={18} />
                      </button>
                      <button className="visita-action-btn access" title="Generar Acceso" onClick={() => handleDarAcceso(visita)}>
                        <IoQrCodeOutline size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* --- Updated AccesoModal Render --- */}
        {currentAcceso && (
          <AccesoModal
            acceso={currentAcceso}
            onClose={() => {
              setCurrentAcceso(null);
              setShowSuccessBanner(false); // Reset banner on close
            }}
            showSuccess={showSuccessBanner} // Pass prop
          />
        )}

        {/* --- Historial Modal --- */}
        {visitaHistorial && (
          <HistorialVisitaModal
            visita={visitaHistorial}
            onClose={() => setVisitaHistorial(null)}
          />
        )}

        {/* --- Custom Confirmation Modal --- */}
        {confirmVisita && (
          <div className="visitas-modal-overlay">
            <div className="visitas-confirm-modal animate-pop">
              <div className="visitas-confirm-icon-wrapper">
                <IoQrCodeOutline size={32} />
              </div>
              <h3>Confirmar Acceso</h3>
              <p>
                ¿Estás seguro de generar un nuevo código de acceso QR temporal para <strong>{confirmVisita.nombreCompleto}</strong>?
              </p>
              <div className="visitas-confirm-actions">
                <button className="confirm-btn cancel" onClick={() => setConfirmVisita(null)}>
                  Cancelar
                </button>
                <button className="confirm-btn accept" onClick={handleConfirmAcceso}>
                  Generar Acceso
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <KondoBottomNav activeRoute="/accesos" />
    </>
  );
}

export default VisitasScreen;